import React, { ReactNode, useState, useEffect } from "react";
import { GridToolbarContainer } from "@mui/x-data-grid";
import { Button, Menu, MenuItem } from "@mui/material";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/pro-solid-svg-icons";
import { AnimatePresence, motion } from "framer-motion";

export type DataGridToolbarAction = {
  icon?: ReactNode;
  label: string;
  onClick: () => void;
};

type DataGridToolbarProps = {
  actions?: DataGridToolbarAction[];
  select: number;
  onSelectAll: (selectAllItems: boolean) => void;
  data: number;
};

export default function DataGridToolbar({
  actions,
  data,
  onSelectAll,
  select,
}: DataGridToolbarProps) {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const open = Boolean(anchorEl);
  const { t } = useTranslation();


  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    event.preventDefault();
    if (!actions) return;

    const handleArrowDown = () => {
      setHighlightedIndex((prevIndex) => (prevIndex + 1) % actions.length);
    };

    const handleArrowUp = () => {
      if (highlightedIndex === -1) {
        setHighlightedIndex(actions.length);
      }
      setHighlightedIndex(
        (prevIndex) => (prevIndex - 1 + actions.length) % actions.length
      );
    };

    const handleEnter = () => {
      if (highlightedIndex >= 0) {
        actions[highlightedIndex].onClick();
        setTimeout(() => {
          handleClose();
        }, 100);
      }
    };

    const handleEscapeOrTab = () => {
      handleClose();
    };

    const keyHandlers: Record<string, () => void> = {
      ArrowDown: handleArrowDown,
      ArrowUp: handleArrowUp,
      Enter: handleEnter,
      Escape: handleEscapeOrTab,
      Tab: handleEscapeOrTab,
    };

    const handler = keyHandlers[event.key];
    if (!handler) return;
    handler();
  };

  return (
    <GridToolbarContainer>
      {actions && actions.length > 0 && (
        <div className="pt-2 pb-3 flex items-center">
          <Button
            className={`${open ? "bg-gray-300" : "bg-white"} hover:bg-blue-500 text-black hover:text-white py-2 rounded-[0.4rem]`}
            style={{ border: select < 1 ? "1px solid transparent" : "1px solid #D7E3F2" }}
            disabled={select < 1}
            onClick={handleClick}
            size="small"
            variant="contained"
            sx={{
              ml: 1,
              mr: 2,
              padding: "0.5rem 1rem",
            }}
          >
            <FontAwesomeIcon
              icon={faChevronDown}
              className={`mr-2 ${open && "rotate-180"}`}
            />
            {t("datagrid:actions")}
            <AnimatePresence>
              {open && (
                <motion.div
                  initial={{ y: 50, rotate: 45 }}
                  animate={{ y: 0, rotate: 45 }}
                  exit={{ y: 50, rotate: 45 }}
                  transition={{ duration: 0.2 }}
                  className="w-5 h-5 bg-white border-[1px] border-[#D7E3F2] absolute -bottom-[22px]"
                />
              )}
            </AnimatePresence>
          </Button>
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            onClick={handleClose}
            onKeyDown={handleKeyDown}
            transformOrigin={{ horizontal: "center", vertical: "top" }}
            anchorOrigin={{ horizontal: "center", vertical: "bottom" }}
            autoFocus={false}
            PaperProps={{
              elevation: 0,
              sx: {
                borderRadius: "0.4rem",
                border: "1px solid #D7E3F2",
                mt: 1,
              },
            }}
          >
            {actions.map((action, index) => (
              <MenuItem
                key={action.label}
                onClick={() => action.onClick()}
                className={`h-8 px-5 my-1 ${
                  highlightedIndex === index ? "bg-blue-50" : ""
                }`}
              >
                <div className="flex items-center w-full border-b-[1px] border-[#dbdbdb]">
                  <div className="flex items-center w-full border-b-[1px] border-[#dbdbdb]">
                    {action.icon && <span className="mx-2">{action.icon}</span>}
                    <div className="py-1 px-1">{action.label}</div>
                  </div>
                </div>
              </MenuItem>
            ))}
          </Menu>

          <Button
            variant="contained"
            className="bg-white rounded-[0.4rem] hover:bg-blue-500 text-black hover:text-white px-3 h-10"
            style={{ border: "1px solid #D7E3F2" }}
            onClick={() => onSelectAll(select >= data ? false : true)}
          >
            {select >= data
              ? t("datagrid:deselectAll")
              : t("datagrid:selectAll")}
          </Button>

          <button
            style={{
              overflow: "hidden",
              border: "1px solid #D7E3F2",
              fontFamily: "Inter",
            }}
            className="absolute pointer-events-none right-3 bg-brand opacity-0 focus:opacity-100 rounded-[0.4rem] text-white hover:text-white py-2 px-3"
            onClick={() => {
              if (window.location.hash === "#skipTable") {
                window.location.hash = "";
              }
              window.location.hash = "skipTable";
            }}
          >
            {t("skipTable")}
          </button>
        </div>
      )}
    </GridToolbarContainer>
  );
}
